import type { ProgramInviteType, UserList } from 'models';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Checkbox,
  type CheckboxChangeEvent,
  Col,
  InfinityLoading,
  Input,
  Loading,
  MultiValueInput,
  Row,
  Search2LineIcon,
  Section,
  Segmented,
  Text,
} from 'ui';
import { images } from 'common/utils';
import type { EmailSelectedUser, RegisteredSelectedUser } from '../types';

export type InviteesType = 'email' | 'registered';

type P = {
  allowedInvitees?: InviteesType[];
  // TODO: type
  allUsers?: UserList[];
  chosenEmails?: EmailSelectedUser[];
  chosenRegistered?: RegisteredSelectedUser[];
  fetchNextPage?: () => unknown;
  hasNextPage?: boolean;
  inviteType: ProgramInviteType;
  isFetching: boolean;
  isLoading: boolean;
  isRefetching: boolean;
  onBack?: () => void;
  onInvite: (
    inviteType: ProgramInviteType,
    invitees: RegisteredSelectedUser[],
    emailInvitees?: EmailSelectedUser[],
  ) => void;
  onSearch: (value: string) => void;
};

const defaultAllowedInvitees: InviteesType[] = ['registered', 'email'];

// TODO search
const EventInvitees = ({
  allowedInvitees = defaultAllowedInvitees,
  allUsers,
  chosenEmails,
  chosenRegistered,
  fetchNextPage,
  hasNextPage,
  inviteType,
  isFetching,
  isLoading,
  // TODO: Why not used?
  isRefetching: _,
  onBack,
  onInvite,
  onSearch,
}: P) => {
  const { t } = useTranslation();
  const [type, setType] = useState(allowedInvitees[0]);
  const [invitees, setInvitees] = useState(chosenRegistered ?? []);
  const [emailInvitees, setEmailInvitees] = useState(chosenEmails ?? []);

  return (
    <>
      {allowedInvitees.length > 1 && (
        <Segmented
          block
          onChange={(value) => {
            setType(value);
          }}
          options={allowedInvitees.map((inviteeType) => ({
            label: inviteeType === 'registered' ? t('Registered') : t('By e-mail'),
            value: inviteeType,
          }))}
          size="large"
          value={type}
        />
      )}
      <Section data-test-id="event-invitees_content" paddingBottom={false}>
        {type === 'registered' && (
          <div className="user-list">
            <Input
              id="input_search-users"
              onChange={(event) => {
                onSearch(event.target.value);
              }}
              placeholder={t('Search...')}
              suffix={<Search2LineIcon />}
            />

            <div className="user-list__list" id="user-list">
              {isLoading || !allUsers ? (
                <Loading />
              ) : // eslint-disable-next-line sonarjs/no-nested-conditional -- TODO: fix later
              allUsers.length === 0 ? (
                <div className="user-list__list__empty">{t('No matches.')}</div>
              ) : (
                <>
                  {allUsers.map((user, index) => (
                    <label className="user-list__list__item" id={`user-${index}`} key={user.id}>
                      <Checkbox
                        checked={invitees.some((invitee) => invitee.id === user.id)}
                        onChange={(event: CheckboxChangeEvent) => {
                          if (event.target.checked) {
                            setInvitees((actualInvitees) => [...actualInvitees, { ...user }]);
                          } else {
                            setInvitees((actualInvitees) => actualInvitees.filter((invitee) => invitee.id !== user.id));
                          }
                        }}
                      />
                      <div
                        className="user-list__list__item__image"
                        style={{ backgroundImage: `url(${user.profilePhoto?.url ?? images.default.userProfilePhoto})` }}
                      />
                      <span className="user-list__list__item__name">{`${user.firstName} ${user.lastName}`}</span>
                    </label>
                  ))}
                  <InfinityLoading
                    hasMoreData={hasNextPage}
                    isLoading={isFetching}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO: fix
                    onEnter={fetchNextPage!}
                    spinnerSize="small"
                  />
                </>
              )}
            </div>
          </div>
        )}
        {type === 'email' && (
          <MultiValueInput
            onChange={(emails: string[]) => {
              setEmailInvitees(emails.map((email) => ({ email })));
            }}
            placeholder={t('Email addresses...')}
            style={{ marginBottom: '32px', minHeight: '254px' }}
            value={emailInvitees.flatMap((emailInvitee) => emailInvitee.email)}
          />
        )}
      </Section>

      <Section lineBottom={false} paddingBottom={false}>
        <Row align="middle" gutter="xs" justify="end">
          <Col>
            {allowedInvitees.includes('registered') && <Text>{t('registeredUsers', { number: invitees.length })}</Text>}
          </Col>
          <Col>
            {/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases */}
            {allowedInvitees.includes('email') && <Text>{t('byEmail', { number: emailInvitees?.length })}</Text>}
          </Col>
          <Col>
            {onBack ? (
              <Button
                onClick={() => {
                  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- TODO: disabled by automatic tool, need to check if it could be fixed or stay ignored in some rare cases
                  onBack?.();
                }}
              >
                Back
              </Button>
            ) : null}
          </Col>
          <Col>
            <Button
              onClick={() => {
                onInvite(inviteType, invitees, emailInvitees);
              }}
              type="primary"
            >
              Invite
            </Button>
          </Col>
        </Row>
      </Section>
    </>
  );
};

export default EventInvitees;
